import { useMemo } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import { Select, Spinner } from "@components/v2";
import { PrimaryButton, TertiaryButton } from "@components/v2/buttons";
import {
  useAdoptedBenchmark,
  useUniversalBenchmarksModalContext,
} from "@features/universal-benchmarks";
import {
  GET_UNIVERSAL_BENCHMARKS_AD_SPEND_AGGREGATOR,
  GET_UNIVERSAL_BENCHMARKS_LEVEL_AGGREGATOR,
} from "@gql/universal-bentchmarks";
import { useNorthbeamQuery } from "@hooks/http";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  type GetUniversalBenchmarksAdSpendAggregator,
  type GetUniversalBenchmarksAdSpendAggregatorVariables,
  type GetUniversalBenchmarksLevelAggregator,
  type GetUniversalBenchmarksLevelAggregatorVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { formatting } from "@north-beam/nb-common";

import { UNIVERSAL_BENCHMARKS_STAGE_OPTIONS } from "../constants";
import { useSalesV4Context, useTableStateDimensions } from "../hooks";

import { Gauge } from "./gauge";
import { Stat } from "./stat";

export const ProfitabilityGauge = () => {
  const { openUniversalBenchmarksModal } = useUniversalBenchmarksModalContext();
  const adoptedBenchmark = useAdoptedBenchmark();
  const { user } = useUser();
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();
  const {
    tableStates,
    dateRangeInterval,
    universalBenchmarksStage,
    setUniversalBenchmarksStage,
  } = useSalesV4Context();

  const tableState = tableStates[0];
  const { breakdownDimension } = useTableStateDimensions(tableState);

  const getTabLabel = (tab: string) => {
    switch (tab) {
      case "breakdown":
        return breakdownDimension.name;
      case "campaign":
        return "Campaigns";
      case "adset":
        return "Adsets";
      case "ad":
        return "Ads";
      default:
        return tab;
    }
  };

  const { data: spendAggregatorData, loading: spendAggregatorLoading } =
    useNorthbeamQuery<
      GetUniversalBenchmarksAdSpendAggregator,
      GetUniversalBenchmarksAdSpendAggregatorVariables
    >(GET_UNIVERSAL_BENCHMARKS_AD_SPEND_AGGREGATOR, {
      skip: !adoptedBenchmark,
      variables: {
        adoptedBenchmarkId: adoptedBenchmark?.id,
        attributionModel,
        accountingMode,
        attributionWindow,
        dateRange: dateRangeInterval,
      },
    });

  const { data: levelAggregatorData, loading: levelAggregatorLoading } =
    useNorthbeamQuery<
      GetUniversalBenchmarksLevelAggregator,
      GetUniversalBenchmarksLevelAggregatorVariables
    >(GET_UNIVERSAL_BENCHMARKS_LEVEL_AGGREGATOR, {
      skip: !adoptedBenchmark,
      variables: {
        adoptedBenchmarkId: adoptedBenchmark?.id,
        level: tableState.selectedTab,
        attributionModel,
        accountingMode,
        attributionWindow,
        dateRange: dateRangeInterval,
      },
    });

  const { allSpend, profitableSpend, unprofitableSpend } = useMemo(() => {
    if (universalBenchmarksStage === "blended") {
      return {
        allSpend:
          spendAggregatorData?.me.universalBenchmarksAdSpendAggregator
            .allSpend ?? 0,
        profitableSpend:
          spendAggregatorData?.me.universalBenchmarksAdSpendAggregator
            .profitableSpend ?? 0,
        unprofitableSpend:
          spendAggregatorData?.me.universalBenchmarksAdSpendAggregator
            .unprofitableSpend ?? 0,
      };
    } else if (universalBenchmarksStage === "new") {
      return {
        allSpend:
          spendAggregatorData?.me.universalBenchmarksAdSpendAggregator
            .allSpend ?? 0,
        profitableSpend:
          spendAggregatorData?.me.universalBenchmarksAdSpendAggregator
            .profitableSpendFt ?? 0,
        unprofitableSpend:
          spendAggregatorData?.me.universalBenchmarksAdSpendAggregator
            .unprofitableSpendFt ?? 0,
      };
    } else if (universalBenchmarksStage === "returning") {
      return {
        allSpend:
          spendAggregatorData?.me.universalBenchmarksAdSpendAggregator
            .allSpend ?? 0,
        profitableSpend:
          spendAggregatorData?.me.universalBenchmarksAdSpendAggregator
            .profitableSpendRtn ?? 0,
        unprofitableSpend:
          spendAggregatorData?.me.universalBenchmarksAdSpendAggregator
            .unprofitableSpendRtn ?? 0,
      };
    }

    return { allSpend: 0, profitableSpend: 0, unprofitableSpend: 0 };
  }, [spendAggregatorData, universalBenchmarksStage]);

  const { profitableRows, unprofitableRows } = useMemo(() => {
    if (universalBenchmarksStage === "blended") {
      return {
        profitableRows:
          levelAggregatorData?.me.universalBenchmarksLevelAggregator
            .profitableRows ?? 0,
        unprofitableRows:
          levelAggregatorData?.me.universalBenchmarksLevelAggregator
            .unprofitableRows ?? 0,
      };
    } else if (universalBenchmarksStage === "new") {
      return {
        profitableRows:
          levelAggregatorData?.me.universalBenchmarksLevelAggregator
            .profitableRowsFt ?? 0,
        unprofitableRows:
          levelAggregatorData?.me.universalBenchmarksLevelAggregator
            .unprofitableRowsFt ?? 0,
      };
    } else if (universalBenchmarksStage === "returning") {
      return {
        profitableRows:
          levelAggregatorData?.me.universalBenchmarksLevelAggregator
            .profitableRowsRtn ?? 0,
        unprofitableRows:
          levelAggregatorData?.me.universalBenchmarksLevelAggregator
            .unprofitableRowsRtn ?? 0,
      };
    }

    return { profitableRows: 0, unprofitableRows: 0 };
  }, [levelAggregatorData, universalBenchmarksStage]);

  return (
    <div
      className={clsx(
        "p-4 border border-v2-tertiary rounded-lg bg-v2-primary text-v2-primary h-full w-full",
        "flex flex-col",
      )}
    >
      <div className="flex items-center justify-between">
        <TertiaryButton
          className="px-[0px] text-base text-v2-primary group"
          onClick={() => {
            openUniversalBenchmarksModal();
          }}
        >
          Profitability
          <i className="fa-regular fa-arrow-up-right-from-square text-sm text-v2-secondary group-hover:text-v2-action"></i>
        </TertiaryButton>
        <Select
          buttonClassName="w-28"
          disabled={!adoptedBenchmark}
          options={UNIVERSAL_BENCHMARKS_STAGE_OPTIONS}
          value={universalBenchmarksStage}
          onChange={(val) => {
            if (val) {
              setUniversalBenchmarksStage(val);
            }
          }}
        />
      </div>
      <div
        className={clsx("relative flex items-center justify-center", {
          "mt-16": adoptedBenchmark === null,
          "mt-12": adoptedBenchmark !== null,
        })}
      >
        <Gauge
          percent={(allSpend === 0 ? 0 : profitableSpend / allSpend) * 100}
          disabled={adoptedBenchmark === null}
        />
        <div className="absolute w-full bottom-0 mb-[20px] text-center">
          <div className="text-2xl font-black">
            {Math.round(
              (allSpend === 0 ? 0 : profitableSpend / allSpend) * 100,
            )}
            %
          </div>
          <div className="text-2xs font-semibold">of spend is profitable</div>
        </div>
      </div>
      {!adoptedBenchmark ? (
        <div className="text-center mt-10">
          <div className="text-center text-sm font-semibold mb-2">
            No benchmark has been set
          </div>
          <p className="text-sm text-v2-secondary mb-6">
            To get the most out of Northbeam,
            <br />
            we need to know a bit about your
            <br />
            business.
          </p>
          <PrimaryButton
            className="text-sm"
            onClick={() => {
              openUniversalBenchmarksModal();
            }}
          >
            Set Benchmark
          </PrimaryButton>
        </div>
      ) : (
        <>
          {spendAggregatorLoading || levelAggregatorLoading ? (
            <div className="h-full flex items-center justify-center">
              <Spinner className="text-v2-action" />
            </div>
          ) : (
            <div className="h-full flex items-end">
              <div className="w-full flex flex-col gap-5">
                <div className="flex flex-col border-b border-v2-tertiary px-3 pb-3">
                  <div className="text-v2-primary text-2xs font-semibold">
                    Profitable Spend
                  </div>
                  <div className="text-v2-primary text-lg font-semibold">
                    {formatting.formatCurrency(profitableSpend, {
                      precision: "exact",
                      code: user.currencies.currentCurrency,
                    })}
                  </div>
                </div>
                <div className="flex flex-col border-b border-v2-tertiary px-3 pb-3">
                  <div className="text-v2-primary text-xxs font-semibold">
                    Unprofitable Spend
                  </div>
                  <div className="text-v2-primary text-lg font-semibold">
                    {formatting.formatCurrency(unprofitableSpend, {
                      precision: "exact",
                      code: user.currencies.currentCurrency,
                    })}
                  </div>
                </div>
                <div className="flex flex-col gap-0">
                  <div className="px-3 capitalize text-2xs font-semibold">
                    {getTabLabel(tableState.selectedTab)}
                  </div>
                  <div className="flex justify-between px-3">
                    <Stat title="Above Benchmark" count={profitableRows} />
                    <Stat
                      justifyNumberRight
                      title="Below Benchmark"
                      count={unprofitableRows}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
